import React from 'react';
import { Button, IconCopy, useTheme } from '@veneer/core';

import ApiError from '../../types/error';
import { handleCopyCode } from '../../utils/common';
import { useDependencyManagerContext } from '../../contexts/dependencyManager';
import {
  InlineNotificationContainer,
  StyledInlineNotification,
  StyledTextbox,
  TextBoxContainer
} from '../device-item/styled';

export type ErrorDisplayProps = {
  error: ApiError;
  dataTestId?: string;
  retryAction?: () => void;
};

export function ErrorDisplay(
  props: Readonly<ErrorDisplayProps>
): React.ReactElement {
  const { error, dataTestId, retryAction } = props;
  const { translate } = useDependencyManagerContext();
  const theme = useTheme();

  const handleRetryAction = () => {
    if (retryAction) {
      retryAction();
    }
  };

  const isDeviceMappingError =
    error.message === 'api-errors.devices.device-mapping.generic-error';

  const isDeviceTelemetryError =
    error.message === 'api-errors.telemetry.device-telemetry.generic-error';

  const getFailedRequestTraceId = () => {
    return error.trace_id || '';
  };

  const getErrorLocalePrefix = () => {
    if (isDeviceMappingError) return 'api-errors.devices.device-mapping';
    return 'api-errors.telemetry.device-telemetry';
  };

  return (
    <StyledInlineNotification
      actionPlacement="top"
      closeButton={false}
      customStyle={{ width: '100%' }}
      description={
        isDeviceMappingError || isDeviceTelemetryError ? (
          <InlineNotificationContainer $veneerTheme={theme}>
            <p style={{ textAlign: 'left' }}>
              {translate(`${getErrorLocalePrefix()}.generic-error-2`)}
            </p>
            <TextBoxContainer>
              <StyledTextbox
                defaultValue={getFailedRequestTraceId() || ''}
                aria-label={translate(
                  `${getErrorLocalePrefix()}.textbox-aria-label`
                )}
                suffix={
                  <Button
                    appearance="ghost"
                    small
                    leadingIcon={<IconCopy />}
                    onClick={() => handleCopyCode(getFailedRequestTraceId())}
                    aria-label={translate(
                      `${getErrorLocalePrefix()}.copy-button-aria-label`
                    )}
                  />
                }
                disabled
                $veneerTheme={theme}
              />
            </TextBoxContainer>
          </InlineNotificationContainer>
        ) : (
          translate(error.message)
        )
      }
      type="negative"
      data-testid={dataTestId}
      action={{
        action: handleRetryAction,
        label: translate('components.device-category.button.retry-button')
      }}
    />
  );
}
